import { useCallback, useSyncExternalStore } from 'react';

// productId / skuId are STRINGS (template/Square SKU ids). productId is the
// PURCHASABLE id sent to /api/checkout/square — for a variant product that's the
// selected child SKU id, for a simple product it's the product id. skuId is kept
// for display/matching only.
export interface CartItem {
  productId: string;
  skuId: string | null;
  skuLabel: string | null;
  name: string;
  price: number;
  image: string;
  quantity: number;
}

const CART_KEY = 'spinworld-cart';

function readCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? (JSON.parse(raw) as CartItem[]) : [];
  } catch {
    return [];
  }
}

function writeCart(cart: CartItem[]) {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  } catch {
    /* ignore quota/availability errors */
  }
}

// ── Module-level shared store ──────────────────────────────────────────────
// Previously useCart used per-component useState, so the Header's badge never
// updated when an item was added from the product/shop page (each hook instance
// had its own state, synced only through localStorage on next mount). The store
// below is shared across every useCart() caller via useSyncExternalStore, so the
// cart badge updates live the moment an item is added — and stays in sync across
// browser tabs through the `storage` event.
let cart: CartItem[] = readCart();
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function commit(next: CartItem[]) {
  cart = next;
  writeCart(next);
  emit();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): CartItem[] {
  return cart;
}

// Cross-tab sync: another tab wrote the cart -> refresh ours and notify.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === CART_KEY) {
      cart = readCart();
      emit();
    }
  });
}

const sameLine = (c: CartItem, productId: string, skuId: string | null) =>
  c.productId === productId && (skuId === null ? c.skuId === null : c.skuId === skuId);

function addItem(item: Omit<CartItem, 'quantity'>, qty = 1) {
  const existing = cart.find((c) => sameLine(c, item.productId, item.skuId));
  if (existing) {
    commit(cart.map((c) => (sameLine(c, item.productId, item.skuId) ? { ...c, quantity: c.quantity + qty } : c)));
  } else {
    commit([...cart, { ...item, quantity: qty }]);
  }
}

function removeItem(productId: string, skuId: string | null = null) {
  commit(cart.filter((c) => !sameLine(c, productId, skuId)));
}

function updateQuantity(productId: string, skuId: string | null, quantity: number) {
  if (quantity <= 0) {
    commit(cart.filter((c) => !sameLine(c, productId, skuId)));
    return;
  }
  commit(cart.map((c) => (sameLine(c, productId, skuId) ? { ...c, quantity } : c)));
}

function clearCart() {
  commit([]);
}

export function useCart() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const cartCount = snapshot.reduce((sum, c) => sum + c.quantity, 0);
  const cartTotal = snapshot.reduce((sum, c) => sum + c.price * c.quantity, 0);

  return {
    cart: snapshot,
    addItem: useCallback(addItem, []),
    removeItem: useCallback(removeItem, []),
    updateQuantity: useCallback(updateQuantity, []),
    clearCart: useCallback(clearCart, []),
    cartCount,
    cartTotal,
  };
}
